import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Divider,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { eventsAPI, locationsAPI, type EventType, type City, type District, type MetroStation } from '../services/api';
import ReCaptchaWidget from '../components/ReCaptchaWidget';

interface EventFormData {
  title: string;
  type_id: number | '';
  start_datetime: string;
  city_id: number | '';
  district_id: number | '';
  metro_station_id: number | '';
  exact_address: string;
  meeting_place: string;
  description: string;
  max_participants: number;
  min_participants: number;
  reserved_seats: number;
  price: number;
  is_public: boolean;
}

const CreateEventPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Состояния для справочников
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [metroStations, setMetroStations] = useState<MetroStation[]>([]);
  
  // Состояния для формы
  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    type_id: '',
    start_datetime: '',
    city_id: '',
    district_id: '',
    metro_station_id: '',
    exact_address: '',
    meeting_place: '',
    description: '',
    max_participants: 10,
    min_participants: 1,
    reserved_seats: 0,
    price: 0,
    is_public: true,
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [createCaptchaRequired, setCreateCaptchaRequired] = useState(false);
  const [createCaptchaResetKey, setCreateCaptchaResetKey] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // Загрузка справочников
  useEffect(() => {
    const loadDictionaries = async () => {
      try {
        const [types, allCities] = await Promise.all([
          locationsAPI.getEventTypes(),
          locationsAPI.getAllCities(),
        ]);
        setEventTypes(types);
        setCities(allCities);

        // Заполнение из sessionStorage (для повторного создания)
        if (searchParams.get('repeat') === '1') {
          const repeatDataStr = sessionStorage.getItem('repeat_event_data');
          
          if (repeatDataStr) {
            try {
              const repeatData = JSON.parse(repeatDataStr);
              
              setFormData(prev => ({
                ...prev,
                title: repeatData.title || '',
                type_id: repeatData.type_id || '',
                city_id: repeatData.city_id || '',
                district_id: repeatData.district_id || '',
                metro_station_id: repeatData.metro_station_id || '',
                exact_address: repeatData.exact_address || '',
                meeting_place: repeatData.meeting_place || '',
                description: repeatData.description || '',
                max_participants: repeatData.max_participants || 10,
                min_participants: repeatData.min_participants || 1,
                reserved_seats: repeatData.reserved_seats || 0,
                price: repeatData.price || 0,
                is_public: repeatData.is_public !== undefined ? repeatData.is_public : true,
              }));
              
              // Очищаем sessionStorage после использования
              sessionStorage.removeItem('repeat_event_data');

              console.log('Данные мероприятия загружены для повтора:', repeatData);
            } catch (error) {
              console.error('Ошибка парсинга данных для повтора:', error);
            }
          }
        }
      } catch (error) {
        console.error('Failed to load dictionaries:', error);
      }
    };

    loadDictionaries();
  }, [searchParams]);
  
  // Загрузка районов при выборе города
  useEffect(() => {
    if (formData.city_id) {
      locationsAPI.getDistricts(Number(formData.city_id))
        .then(setDistricts)
        .catch(console.error);
    } else {
      setDistricts([]);
      setFormData(prev => ({ ...prev, district_id: '', metro_station_id: '' }));
    }
  }, [formData.city_id]);
  
  // Загрузка метро при выборе города
  useEffect(() => {
    if (formData.city_id) {
      locationsAPI.getMetroStations(Number(formData.city_id))
        .then(setMetroStations)
        .catch(console.error);
    } else {
      setMetroStations([]);
      setFormData(prev => ({ ...prev, metro_station_id: '' }));
    }
  }, [formData.city_id]);
  
  const handleChange = (field: keyof EventFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Очищаем ошибку при изменении
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };
  
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.title || formData.title.length < 3) {
      newErrors.title = 'Название должно быть не менее 3 символов';
    }
    
    if (!formData.type_id) {
      newErrors.type_id = 'Выберите вид мероприятия';
    }
    
    if (!formData.start_datetime) {
      newErrors.start_datetime = 'Укажите дату и время';
    } else {
      const eventDate = new Date(formData.start_datetime);
      const now = new Date();
      if (eventDate <= now) {
        newErrors.start_datetime = 'Мероприятие не может быть в прошлом';
      }
    }
    
    if (!formData.city_id) {
      newErrors.city_id = 'Выберите город';
    }
    
    if (!formData.max_participants || formData.max_participants < 1) {
      newErrors.max_participants = 'Минимум 1 участник';
    }
    
    if (formData.min_participants > formData.max_participants) {
      newErrors.min_participants = 'Минимум не может быть больше максимума';
    }
    
    if (formData.reserved_seats > formData.max_participants) {
      newErrors.reserved_seats = 'Зарезервированные места не могут превышать максимум';
    }
    
    if (formData.price < 0) {
      newErrors.price = 'Цена не может быть отрицательной';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (captchaToken?: string) => {
    if (!validateForm()) {
      setSnackbar({ open: true, message: 'Исправьте ошибки в форме', severity: 'error' });
      return;
    }
    
    try {
      setLoading(true);
      
      const eventData = {
        title: formData.title,
        type_id: Number(formData.type_id),
        start_datetime: formData.start_datetime,
        city_id: Number(formData.city_id),
        district_id: formData.district_id ? Number(formData.district_id) : null,
        metro_station_id: formData.metro_station_id ? Number(formData.metro_station_id) : null,
        exact_address: formData.exact_address,
        meeting_place: formData.meeting_place,
        description: formData.description,
        max_participants: formData.max_participants,
        min_participants: formData.min_participants,
        reserved_seats: formData.reserved_seats,
        price: formData.price,
        is_public: formData.is_public,
      };
      
      const result = await eventsAPI.createEvent(eventData, captchaToken);

      if (result?.captcha_required) {
        setCreateCaptchaRequired(true);
        setSnackbar({ open: true, message: result.message || 'Требуется проверка CAPTCHA', severity: 'error' });
        setLoading(false);
        return;
      }

      setCreateCaptchaRequired(false);
      setSnackbar({ open: true, message: 'Мероприятие создано!', severity: 'success' });
      
      setTimeout(() => {
        navigate(`/events/${result.event_id}`);
      }, 1000);
      
    } catch (err: any) {
      console.error('Failed to create event:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Ошибка при создании мероприятия',
        severity: 'error',
      });
      if (createCaptchaRequired) setCreateCaptchaResetKey(k => k + 1);
    } finally {
      setLoading(false);
    }
  };
  
  const handleReset = () => {
    setFormData({
      title: '',
      type_id: '',
      start_datetime: '',
      city_id: '',
      district_id: '',
      metro_station_id: '',
      exact_address: '',
      meeting_place: '',
      description: '',
      max_participants: 10,
      min_participants: 1,
      reserved_seats: 0,
      price: 0,
      is_public: true,
    });
    setErrors({});
  };
  
  // Минимальная дата - текущее время
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  const minDateTime = now.toISOString().slice(0, 16);
  
  return (
    <Box>
      {/* Заголовок */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/events')} size="small">
            Назад
          </Button>
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ fontSize: { xs: '1.2rem', sm: '1.8rem' } }}>
            ➕ Создать мероприятие
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ClearIcon />}
          onClick={handleReset}
          size="small"
        >
          Сбросить
        </Button>
      </Box>
      
      <Grid container spacing={3}>
        {/* Основная форма */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              🎯 Основная информация
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Название мероприятия"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  error={!!errors.title}
                  helperText={errors.title || `Символов: ${formData.title.length}`}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  fullWidth
                  options={eventTypes}
                  getOptionLabel={(option) => `${option.icon_emoji || '🎯'} ${option.name}`}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <Box component="li" key={key} {...otherProps}>
                        <Typography sx={{ fontSize: '1.2rem', mr: 1 }}>
                          {option.icon_emoji || '🎯'}
                        </Typography>
                        {option.name}
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Вид мероприятия"
                      placeholder="Поиск вида спорта..."
                      required
                      error={!!errors.type_id}
                      helperText={errors.type_id}
                    />
                  )}
                  value={eventTypes.find((t) => t.id === formData.type_id) || null}
                  onChange={(_, newValue) => {
                    handleChange('type_id', newValue?.id || '');
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  noOptionsText="Не найдено"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="datetime-local"
                  label="Дата и время начала"
                  value={formData.start_datetime}
                  onChange={(e) => handleChange('start_datetime', e.target.value)}
                  error={!!errors.start_datetime}
                  helperText={errors.start_datetime}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: minDateTime }}
                  sx={{ '& input[type="datetime-local"]': { color: errors.start_datetime ? 'error.main' : 'inherit' } }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Autocomplete
                  fullWidth
                  options={cities}
                  getOptionLabel={(option) => `${option.name} ${option.region ? `(${option.region})` : ''}`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Город"
                      placeholder="Поиск города..."
                      required
                      error={!!errors.city_id}
                      helperText={errors.city_id}
                    />
                  )}
                  value={cities.find((c) => c.id === formData.city_id) || null}
                  onChange={(_, newValue) => {
                    handleChange('city_id', newValue?.id || '');
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  noOptionsText="Не найдено"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  fullWidth
                  options={districts}
                  getOptionLabel={(option) => option.name}
                  disabled={!formData.city_id || districts.length === 0}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Район"
                      placeholder="Поиск района..."
                    />
                  )}
                  value={districts.find((d) => d.id === formData.district_id) || null}
                  onChange={(_, newValue) => {
                    handleChange('district_id', newValue?.id || '');
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  noOptionsText="В этом городе нет районов"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  fullWidth
                  options={metroStations}
                  getOptionLabel={(option) => option.name}
                  disabled={!formData.city_id || metroStations.length === 0}
                  filterOptions={(options, { inputValue }) => {
                    const lowerInput = inputValue.toLowerCase().trim();
                    if (!lowerInput) {
                      return options;
                    }
                    return options.filter(option => 
                      option.name.toLowerCase().includes(lowerInput)
                    );
                  }}
                  renderOption={(props, station, { selected }) => {
                    const { key, ...otherProps } = props;
                    return (
                      <Box 
                        component="li" 
                        key={station.id}
                        {...otherProps} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          bgcolor: selected ? 'action.selected' : 'transparent',
                        }} 
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 4,
                            bgcolor: station.line_color || '#999',
                            borderRadius: 1,
                            flexShrink: 0,
                          }}
                        />
                        <Box sx={{ overflow: 'hidden' }}>
                          <Typography variant="body2" noWrap>
                            {station.name}
                          </Typography>
                          {station.line_name && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {station.line_name}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Станция метро"
                      placeholder="Начните вводить название станции..."
                    />
                  )}
                  value={metroStations.find((s) => s.id === formData.metro_station_id) || null}
                  onChange={(_, newValue) => {
                    handleChange('metro_station_id', newValue?.id || '');
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  noOptionsText={metroStations.length === 0 ? "Сначала выберите город" : "Станция не найдена"}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Точный адрес"
                  value={formData.exact_address}
                  onChange={(e) => handleChange('exact_address', e.target.value)}
                  placeholder="Улица, дом, корпус"
                  helperText="Будет виден только участникам"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Место встречи"
                  value={formData.meeting_place}
                  onChange={(e) => handleChange('meeting_place', e.target.value)}
                  placeholder="Например: у главного входа, у фонтана"
                  helperText="Где участникам встретиться перед началом"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Paper>
          
          {/* Описание */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              📝 Описание
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <TextField
              fullWidth
              multiline
              rows={6}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Опишите ваше мероприятие: цели, план, что нужно взять с собой, уровень подготовки участников..."
              inputProps={{ maxLength: 1000 }}
              helperText={`Символов: ${formData.description.length} / 1000`}
            />
          </Paper>
        </Grid>
        
        {/* Боковая панель */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              👥 Участники
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Минимум"
                  value={formData.min_participants}
                  onChange={(e) => handleChange('min_participants', Number(e.target.value))}
                  onFocus={(e) => e.target.select()}
                  error={!!errors.min_participants}
                  helperText={errors.min_participants}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Максимум"
                  value={formData.max_participants}
                  onChange={(e) => handleChange('max_participants', Number(e.target.value))}
                  onFocus={(e) => e.target.select()}
                  error={!!errors.max_participants}
                  helperText={errors.max_participants}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Зарезервировано"
                  value={formData.reserved_seats}
                  onChange={(e) => handleChange('reserved_seats', Number(e.target.value))}
                  onFocus={(e) => e.target.select()}
                  error={!!errors.reserved_seats}
                  helperText={errors.reserved_seats || 'Места для приглашенных'}
                  inputProps={{ min: 0 }}
                />
              </Grid>
            </Grid>
          </Paper>
          
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              💰 Стоимость
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <TextField
              fullWidth
              type="number"
              label="Цена участия"
              value={formData.price}
              onChange={(e) => handleChange('price', Number(e.target.value))}
              onFocus={(e) => e.target.select()}
              error={!!errors.price}
              helperText={errors.price || '0 = бесплатно'}
              InputProps={{
                endAdornment: <InputAdornment position="end">₽</InputAdornment>,
              }}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Paper>
          
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              🔐 Доступность
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_public}
                  onChange={(e) => handleChange('is_public', e.target.checked)}
                  color="primary"
                />
              }
              label={formData.is_public ? '🌐 Открытое' : '🔒 Закрытое'}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {formData.is_public
                ? 'Все желающие могут записаться автоматически'
                : 'Заявки будут отправляться организатору на подтверждение'}
            </Typography>
          </Paper>
          
          {/* Кнопка создания */}
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<SaveIcon />}
              onClick={() => handleSubmit()}
              disabled={loading}
              sx={{ py: 2 }}
            >
              {loading ? 'Создание...' : '🎉 Создать мероприятие'}
            </Button>
          </Box>

          {createCaptchaRequired && (
            <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Для создания мероприятия требуется проверка CAPTCHA
              </Typography>
              <ReCaptchaWidget
                resetKey={createCaptchaResetKey}
                onVerify={(token) => handleSubmit(token)}
              />
            </Box>
          )}
        </Grid>
      </Grid>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateEventPage;
